import { css } from '@emotion/react';
import { useMemo } from 'react';

import { useManageScanPhotosSync, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import { removeDuplicateScanPhotos } from '@kaiizen/kai-scan-template-system';

import { getFilteredGroups } from '@/Client/Lib/scanLib';
import {
  applyGalleryPhotoOperation,
  buildFocusedGalleryModel,
  buildGalleryPhotoTargets,
  type FocusedGallerySection,
  FocusedPhotoGallery,
  type GalleryScopeTarget,
} from '@/Client/Partials/FocusedPhotoGallery';
import type { ScanFocusPanelScopeProps } from '@/Client/Partials/FocusPanel';
import { useConfirmationModal, useToast } from '@/Client/State';
import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily } from '@kaiizen/kai-shiki/core';
import { CommonActionButton } from '@kaiizen/kai-shiki/react';

const SCAN_STYLE = css`
  label: SCAN_STYLE;
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

const RESYNC_BUTTON_STYLE = {
  position: 'absolute',
  right: 22,
  bottom: 18,
  zIndex: 12,
  minHeight: 32,
  padding: '0 12px',
  border: `1px solid ${styleguideColors.stroke}`,
  borderRadius: 0,
  background: styleguideColors.white,
  color: styleguideColors.textSecondary,
  fontFamily: styleguideFontFamily.mono,
  fontSize: pxToRem(10),
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
} as const;

export const AllScanPhotos = ({
  filterRegex,
  focusPanelScopeNavigation,
  onSelectAll,
  onSelectGroup,
  onSelectTask,
  onActiveScopeChange,
  walkthroughUploadEnabled,
  onUploadWalkthrough,
}: {
  filterRegex?: RegExp;
  focusPanelScopeNavigation?: ScanFocusPanelScopeProps;
  onSelectAll?: () => void;
  onSelectGroup?: (groupId: string) => void;
  onSelectTask?: (taskId: string) => void;
  onActiveScopeChange?: (target: GalleryScopeTarget) => void;
  walkthroughUploadEnabled?: boolean;
  onUploadWalkthrough?: () => void;
}) => {
  const selectedGroupId = focusPanelScopeNavigation?.selectedGroupId;
  const selectedTaskId = focusPanelScopeNavigation?.selectedTaskId;
  const { scanRoot, scan, scanDataMutators, scans } = useScanEditingContextSlice([
    'scanRoot',
    'scan',
    'scanDataMutators',
    'scans',
  ]);
  const model = useMemo(() => {
    if (!scanRoot) return undefined;
    return buildFocusedGalleryModel({
      scanRoot,
      groups: getFilteredGroups(scanRoot.groups, filterRegex),
      selectedGroupId,
      selectedTaskId,
    });
  }, [filterRegex, scanRoot, selectedGroupId, selectedTaskId]);
  const { resyncScanPhotos } = useManageScanPhotosSync();
  const confirm = useConfirmationModal();
  const { showToast, showErrorToast } = useToast();

  const removeDuplicates = async (section: FocusedGallerySection) => {
    const photos = removeDuplicateScanPhotos([...section.photos]);
    const { groupId, taskId } = section.scanPartContext;

    if (groupId && taskId) {
      await scanDataMutators.updateTask(groupId, taskId, { photos });
    } else if (groupId) {
      await scanDataMutators.updateGroup(groupId, { photos });
    } else {
      await scanDataMutators.updateScan({ photos });
    }
  };

  const handleResyncClick = async () => {
    if (!scan) return;

    await confirm({
      title: 'Resync Scan Photos',
      message: 'This will check which photos are synced and queue any missing for resync. Continue?',
      confirmLabel: 'Resync',
      onConfirm: async () => {
        try {
          const result = await resyncScanPhotos(scans?.map(candidate => candidate.id) ?? [scan.id]);
          const { numSynced, numResyncing, errors } = result;
          const parts = [];
          if (numSynced > 0) parts.push(`${numSynced} already synced`);
          if (numResyncing > 0) parts.push(`${numResyncing} queued for resync`);
          showToast(parts.length ? parts.join('. ') : 'No photos to resync.');
          if (errors.length > 0) {
            showErrorToast(
              errors[0],
              errors.length === 1 ? 'queue photo for resync' : `queue ${errors.length} photos for resync`,
            );
          }
        } catch (error) {
          showErrorToast(error, 'resync scan photos');
        }
      },
    });
  };

  if (!scan || !scanRoot || !model) return null;

  return (
    <div css={SCAN_STYLE} data-gallery-workspace-shell='true'>
      <FocusedPhotoGallery
        model={model}
        activePanel={focusPanelScopeNavigation?.activePanel}
        onSelectPanel={focusPanelScopeNavigation?.onSelectPanel}
        management={{
          scanRoot,
          targets: buildGalleryPhotoTargets(scanRoot),
          applyPhotoOperation: async operation => {
            await scanDataMutators.setScan(
              current => applyGalleryPhotoOperation(current, operation),
            );
          },
        }}
        onSelectAll={onSelectAll}
        onSelectGroup={onSelectGroup}
        onSelectTask={onSelectTask}
        onActiveScopeChange={onActiveScopeChange}
        onRemoveDuplicates={removeDuplicates}
        walkthroughUploadEnabled={walkthroughUploadEnabled}
        onUploadWalkthrough={onUploadWalkthrough}
      />
      <CommonActionButton
        aria-label='Resync photos'
        css={RESYNC_BUTTON_STYLE}
        onClick={handleResyncClick}
      >
        Resync photos
      </CommonActionButton>
    </div>
  );
};
